import { useDevRuntimeStore } from '@/modules/dev/devRuntime';
import type { IssueReportCreate } from '@/schema/issueReport';

/** How many dev-log entries to attach. Enough for context, small enough to store as text. */
const LOG_TAIL_SIZE = 60;

/** Canvas captures are downscaled before upload; the backend drops anything over 3 MB. */
const SCREENSHOT_MAX_WIDTH = 1280;

type Diagnostics = Pick<
  IssueReportCreate,
  | 'route'
  | 'patientId'
  | 'scanId'
  | 'appVersion'
  | 'userAgent'
  | 'platform'
  | 'screenInfo'
  | 'gpuVendor'
  | 'gpuRenderer'
  | 'context'
>;

/**
 * Reads the GPU string the driver reports, which is the single most useful field here:
 * most rendering bugs in this app reproduce on one vendor/driver and nowhere else.
 *
 * Creates a throwaway context — the app's own renderer must not be disturbed by a bug
 * report — and gives up quietly if WebGL or the debug extension is unavailable.
 */
function readGpuInfo(): { gpuVendor?: string; gpuRenderer?: string } {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as WebGLRenderingContext | null;
    if (!gl) return {};

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const result = debugInfo
      ? {
          gpuVendor: String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) ?? ''),
          gpuRenderer: String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) ?? ''),
        }
      : {
          gpuVendor: String(gl.getParameter(gl.VENDOR) ?? ''),
          gpuRenderer: String(gl.getParameter(gl.RENDERER) ?? ''),
        };

    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return result;
  } catch {
    return {};
  }
}

/** Pulls patient/scan ids straight out of the URL — every 3D route carries them. */
export function readRouteIds(pathname: string) {
  const patientMatch = pathname.match(/\/patients\/([0-9a-f-]{36})/i);
  const scanMatch = pathname.match(/\/scans\/([0-9a-f-]{36})/i);
  return {
    patientId: patientMatch?.[1],
    scanId: scanMatch?.[1],
  };
}

/**
 * Best-effort snapshot of the moment the problem was reported. Every probe is guarded:
 * a failing one must never stop the report from being sent.
 */
export function collectDiagnostics(extraContext?: Record<string, unknown>): Diagnostics {
  const route = `${window.location.pathname}${window.location.search}`;
  const { patientId, scanId } = readRouteIds(window.location.pathname);
  const { gpuVendor, gpuRenderer } = readGpuInfo();

  let context: string | undefined;
  try {
    const logs = useDevRuntimeStore.getState().logs.slice(-LOG_TAIL_SIZE);
    context = JSON.stringify(
      {
        ...extraContext,
        devLogTail: logs.map((entry) => ({
          time: entry.time,
          kind: entry.kind,
          tag: entry.tag,
          message: entry.message,
        })),
      },
      null,
      0,
    );
  } catch {
    context = undefined;
  }

  return {
    route,
    patientId,
    scanId,
    appVersion: import.meta.env.VITE_APP_VERSION ?? import.meta.env.MODE,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    screenInfo: `${window.screen.width}x${window.screen.height} @${window.devicePixelRatio}x`,
    gpuVendor,
    gpuRenderer,
    context,
  };
}

/**
 * Grabs the largest WebGL canvas on the page.
 *
 * `preserveDrawingBuffer` is off in this app, so the colour buffer is usually already
 * cleared by the time `toDataURL` runs and the capture comes back blank. That is why the
 * screenshot is optional and its absence is never treated as an error.
 */
export async function captureCanvas(): Promise<string | undefined> {
  try {
    const canvases = Array.from(document.querySelectorAll('canvas'));
    const target = canvases.sort((a, b) => b.width * b.height - a.width * a.height)[0];
    if (!target || target.width === 0) return undefined;

    const scale = Math.min(1, SCREENSHOT_MAX_WIDTH / target.width);
    if (scale === 1) return target.toDataURL('image/png');

    const scaled = document.createElement('canvas');
    scaled.width = Math.round(target.width * scale);
    scaled.height = Math.round(target.height * scale);
    const ctx = scaled.getContext('2d');
    if (!ctx) return target.toDataURL('image/png');

    ctx.drawImage(target, 0, 0, scaled.width, scaled.height);
    return scaled.toDataURL('image/png');
  } catch {
    return undefined;
  }
}
